// src/components/FlightStatusCard.js
import React from 'react';
import { Card, CardContent, Typography, Box, Stack } from '@mui/material';

function normalizeStatus(s) {
  switch (s) {
    // New vocabulary
    case 'go':                     return 'go';
    case 'advisory':               return 'advisory';
    case 'caution':                return 'caution';
    case 'no-go':                  return 'no-go';
    case 'unknown':                return 'unknown';
    // Legacy vocabulary (fall-through)
    case 'clear_to_fly':           return 'go';
    case 'clear_with_advisories':  return 'advisory';
    case 'not_recommended':        return 'caution';
    case 'do_not_fly':             return 'no-go';
    case 'illegal_to_fly':         return 'illegal'; // special styling
    default:                       return 'unknown';
  }
}

const FLIGHT_DISPLAY = {
  go: {
    summary: '✅ CLEAR TO FLY',
    detail:  'Conditions are optimal. No known airspace or weather issues.',
    color:   '#00E676',
  },
  advisory: {
    summary: '⚠️ CLEAR TO FLY — ADVISORIES',
    detail:  'Flyable, but there are minor advisories to consider.',
    color:   '#FFEA00',
  },
  caution: {
    summary: '🔶 FLIGHT NOT RECOMMENDED',
    detail:  'Risky flight. Conditions not ideal for your drone.',
    color:   '#FF9100',
  },
  'no-go': {
    summary: '❌ DO NOT FLY',
    detail:  'Conditions are unsafe for flight.',
    color:   '#FF5252',
  },
  illegal: {
    summary: '⛔ DO NOT FLY — LEGAL RESTRICTION',
    detail:  'Airspace restrictions prohibit flight in this area.',
    color:   '#D50000',
  },
  unknown: {
    summary: 'NO DATA YET',
    detail:  'There is not enough data yet to make a firm decision.',
    color:   '#999999',
  },
};

const trafficLights = [
  { key: 'no-go', color: '#FF5252' },
  { key: 'caution', color: '#FF9100' },
  { key: 'go', color: '#00E676' },
];

const labelForPendingKey = key => {
  switch (key) {
    case 'weather': return 'weather';
    case 'tfr': return 'TFRs';
    case 'airspace': return 'airspace';
    case 'NearbyHazards': return 'hazards';
    case 'hazards': return 'hazards';
    default: return key;
  }
};

const FlightStatusCard = ({ status, reason, reasons = [], loading = false, pendingChecks = [] }) => {
  const sev = normalizeStatus(status);
  const cfg = FLIGHT_DISPLAY[sev] ?? FLIGHT_DISPLAY.unknown;
  const reasonList = reasons.length ? reasons : (reason ? [reason] : []);
  const detailText = reasonList.length
    ? cfg.detail
    : cfg.detail;

  return (
    <Card>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 1.25, px: 1.5, '&:last-child': { pb: 1.25 } }}>
        <Stack
          spacing={0.45}
          sx={{
            width: 30,
            p: 0.5,
            borderRadius: 5,
            flexShrink: 0,
            bgcolor: 'rgba(0,0,0,0.28)',
            border: '1px solid rgba(255,255,255,0.16)',
          }}
        >
          {trafficLights.map(light => {
            const active = sev === light.key || (sev === 'illegal' && light.key === 'no-go');
            return (
              <Box
                key={light.key}
                sx={{
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  bgcolor: active ? light.color : 'rgba(255,255,255,0.12)',
                  boxShadow: active ? `0 0 14px ${light.color}` : 'inset 0 0 0 1px rgba(255,255,255,0.12)',
                  transition: 'background-color 180ms ease, box-shadow 180ms ease',
                }}
              />
            );
          })}
        </Stack>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.1 }}>Flight Clearance</Typography>
          <Typography variant="h6" sx={{ color: cfg.color, lineHeight: 1.2 }}>
            {cfg.summary}
          </Typography>
          {(reasonList.length > 0 || loading) && (
            <Typography variant="caption" sx={{ color: '#c1c6d6' }}>
              {loading ? 'Collecting data before recommending a flight status.' : detailText}
            </Typography>
          )}
          {!loading && reasonList.length > 0 && (
            <Stack spacing={0.35} sx={{ mt: 0.65 }}>
              {reasonList.slice(0, 4).map((item, idx) => (
                <Typography
                  key={idx}
                  variant="caption"
                  sx={{
                    color: '#d9deec',
                    lineHeight: 1.25,
                    display: 'block',
                  }}
                >
                  {item}
                </Typography>
              ))}
            </Stack>
          )}
          {loading && (
            <Box
              sx={{
                mt: 0.75,
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                borderTop: '1px solid rgba(255,255,255,0.08)',
                pt: 0.5,
              }}
            >
              <Typography
                variant="caption"
                component="div"
                sx={{
                  color: '#8fa2c8',
                  display: 'inline-block',
                  animation: 'flightiqStatusTicker 13s linear infinite',
                  '@keyframes flightiqStatusTicker': {
                    '0%': { transform: 'translateX(100%)' },
                    '100%': { transform: 'translateX(-100%)' },
                  },
                }}
              >
                Collecting {pendingChecks.length ? pendingChecks.map(labelForPendingKey).join(' • ') : 'flight data'}...
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default FlightStatusCard;
